import {
    DestroyRef,
    Directive,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnChanges,
    OnInit,
    Renderer2,
    SimpleChanges
} from "@angular/core";
import {onEach} from '../utils/observable-utils';
import {PageMode, ResponsiveService} from "./responsive.service";
import {NavigationService} from '../navigation/navigation.service';

@Directive({
    selector: "[woa-responsive]"
})
/**
 * Directive to add responsive classes to an element
 */
export class ResponsiveDirective implements OnInit, OnChanges {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private element: ElementRef = inject(ElementRef);
    private responsiveService: ResponsiveService = inject(ResponsiveService);
    private navigationService: NavigationService = inject(NavigationService);
    private renderer: Renderer2 = inject(Renderer2);

    public readonly pillarboxedOnMobile: InputSignal<"mobile" | "none"> = input<"mobile" | "none">("none");
    public readonly pillarboxedOnDesktop: InputSignal<"mobile" | "desktop" | "double-mobile" | "none"> = input<"mobile" | "desktop" | "double-mobile" | "none">("none");
    public readonly fontScale: InputSignal<number | undefined> = input<number | undefined>();
    private initialised: boolean = false;

    constructor() {
        effect(() => this.applyMode());
        effect(() => {
            this.setAvailableWidth();
            if (this.fontScale() !== undefined) {
                this.setFontSize();
            }
        });
        effect(() => this.setAvailableHeight());
    }

    ngOnInit(): void {
        if (this.fontScale() !== undefined) {
            this.setFontSize();
        }

        if (this.responsiveService.isSafari()) {
            this.renderer.addClass(this.element.nativeElement, "safari");
        } else if (this.responsiveService.isChrome()) {
            this.renderer.addClass(this.element.nativeElement, "chrome");
        } else if (this.responsiveService.isFirefox()) {
            this.renderer.addClass(this.element.nativeElement, "firefox");
        } else if (this.responsiveService.isEdge()) {
            this.renderer.addClass(this.element.nativeElement, "edge");
        } else if (this.responsiveService.isOpera()) {
            this.renderer.addClass(this.element.nativeElement, "opera");
        }

        this.setAvailableWidth();
        this.setAvailableHeight();

        if (this.fontScale() !== undefined) {
            // noinspection JSUnresolvedReference
            new (window as any).ResizeObserver(() => this.setFontSize()).observe(this.element.nativeElement);
            onEach(this.navigationService.pageCreated$, () => this.setFontSize(), this.destroyRef);
        }

        this.initialised = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialised) {
            return;
        }
        if (changes["pillarboxedOnMobile"] || changes["pillarboxedOnDesktop"]) {
            this.setAvailableWidth();
        }
        if (changes["fontScale"]) {
            this.setFontSize();
        }
    }

    private applyMode(): void {
        if (this.responsiveService.mode() === undefined) {
            return;
        }

        if (this.responsiveService.mobile()) {
            this.renderer.removeClass(this.element.nativeElement, "desktop");
            this.renderer.addClass(this.element.nativeElement, "mobile");
        } else {
            this.renderer.removeClass(this.element.nativeElement, "mobile");
            this.renderer.addClass(this.element.nativeElement, "desktop");
        }

        this.renderer.removeClass(this.element.nativeElement, "narrow");
        this.renderer.removeClass(this.element.nativeElement, "basic");
        this.renderer.removeClass(this.element.nativeElement, "wide");
        this.renderer.removeClass(this.element.nativeElement, "wider");
        this.renderer.removeClass(this.element.nativeElement, "widest");

        this.renderer.removeClass(this.element.nativeElement, "small");
        this.renderer.removeClass(this.element.nativeElement, "medium");
        this.renderer.removeClass(this.element.nativeElement, "large");

        this.renderer.removeClass(this.element.nativeElement, "short");

        if (this.responsiveService.desktop()) {
            // On desktop, we determine the page mode based on the desktop pillarboxing width
            const availableWidth = this.responsiveService.calculateAvailableWidth()(this.element.nativeElement, this.pillarboxedOnDesktop());
            const mode: PageMode = this.responsiveService.calculateMode()(availableWidth);
            const responsiveClass: string =
                mode === PageMode.NARROW_DESKTOP ? "narrow"
                    : mode === PageMode.BASIC_DESKTOP ? "basic"
                        : mode === PageMode.WIDE_DESKTOP ? "wide"
                            : mode === PageMode.WIDER_DESKTOP ? "wider"
                                : mode === PageMode.WIDEST_DESKTOP ? "widest"
                                    : undefined;
            if (responsiveClass) {
                this.renderer.addClass(this.element.nativeElement, responsiveClass);
            }
            if (this.responsiveService.short()) {
                this.renderer.addClass(this.element.nativeElement, "short");
            }
        } else {
            // On mobile, we determine the page mode based on the actual window width
            const mode: PageMode = this.responsiveService.calculateMode()(this.responsiveService.windowWidth());
            const responsiveClass: string =
                mode === PageMode.SMALL_MOBILE ? "small"
                    : mode === PageMode.MEDIUM_MOBILE ? "medium"
                        : mode === PageMode.MOBILE ? "large"
                            : undefined;
            if (responsiveClass) {
                this.renderer.addClass(this.element.nativeElement, responsiveClass);
            }
        }

        if (this.fontScale() !== undefined) {
            this.setFontSize();
        }
    }

    private setAvailableWidth(): void {
        if (!this.responsiveService.windowWidth()) {
            return;
        }

        this.element.nativeElement.style.removeProperty("--available-width");
        this.element.nativeElement.style.removeProperty("--pillarbox-gutter-width");
        this.renderer.removeClass(this.element.nativeElement, "pillarboxed");

        const availableWidth = this.responsiveService.calculateAvailableWidth()(this.element.nativeElement, this.responsiveService.desktop() ? this.pillarboxedOnDesktop() : this.pillarboxedOnMobile());
        this.element.nativeElement.style.setProperty("--available-width", availableWidth + "px");
        if (this.responsiveService.desktop() && this.pillarboxedOnDesktop() && this.pillarboxedOnDesktop() !== "none") {
            const gutterWidth: number = (this.responsiveService.windowWidth() - availableWidth) / 2;
            this.element.nativeElement.style.setProperty("--pillarbox-gutter-width", gutterWidth + "px");
            if (gutterWidth) {
                this.renderer.addClass(this.element.nativeElement, "pillarboxed");
            }
        }
    }

    private setAvailableHeight(): void {
        if (this.responsiveService.availableHeight() === undefined) {
            return;
        }

        this.element.nativeElement.style.setProperty("--available-height", this.responsiveService.availableHeight() + "px");
    }

    private setFontSize(): void {
        if (this.fontScale()) {
            setTimeout(() => {
                const fontSize: string = (this.element.nativeElement.offsetWidth * this.fontScale()!) + "px";
                const fontSizeFloored: string = Math.max(1, Math.floor((this.element.nativeElement.offsetWidth * this.fontScale()!))) + "px";
                const fontSizeRounded: string = Math.max(1, Math.round((this.element.nativeElement.offsetWidth * this.fontScale()!))) + "px";
                this.element.nativeElement.style.setProperty("font-size", fontSize);
                this.element.nativeElement.style.setProperty("--woa-font-size", fontSize);
                this.element.nativeElement.style.setProperty("--woa-font-size-floored", fontSizeFloored);
                this.element.nativeElement.style.setProperty("--woa-font-size-rounded", fontSizeRounded);
            });
        } else {
            this.element.nativeElement.style.removeProperty("font-size");
            this.element.nativeElement.style.removeProperty("--woa-font-size");
            this.element.nativeElement.style.removeProperty("--woa-font-size-floored");
            this.element.nativeElement.style.removeProperty("--woa-font-size-rounded");
        }
    }
}
